/**
 * Cron Config Schema
 *
 * Defines the shape of cron job configuration stored in SystemConfig.config JSONB field.
 * Used for validating cron-specific config at runtime.
 */

import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";

/**
 * Cron expression regex - validates basic cron syntax
 *
 * Matches standard 5-field OR extended 6-field cron expressions:
 * - 5-field: minute hour day month weekday
 * - 6-field: second minute hour day month weekday
 *
 * Supports numbers, wildcards (*), step values (star-slash-N), ranges, lists, and combinations.
 *
 * @example "0 2 * * *" - Daily at 2 AM (5-field)
 * @example "0 0 1 * *" - Monthly on the 1st at midnight
 * @example "0 9-17 * * 1-5" - Weekdays 9 AM to 5 PM
 */
const CRON_5_FIELD =
  /^([0-9,\-/*]+)\s+([0-9,\-/*]+)\s+([0-9,\-/*]+)\s+([0-9,\-/*]+)\s+([0-9,\-/*]+)$/;
const CRON_6_FIELD =
  /^([0-9,\-/*]+)\s+([0-9,\-/*]+)\s+([0-9,\-/*]+)\s+([0-9,\-/*]+)\s+([0-9,\-/*]+)\s+([0-9,\-/*]+)$/;

function isValidCronExpression(value: string): boolean {
  return CRON_5_FIELD.test(value) || CRON_6_FIELD.test(value);
}

/**
 * How long a SystemConfig `description` may be — the CONFIG-wide cap, not a
 * cron-only one.
 *
 * 🔴 IT HAS TO BE ONE NUMBER BECAUSE IT HAS BEEN ONE STRING. The boot sync used
 * to write each code default's description into the row's `description` column
 * as well, and rows written then still hold it until an admin clears them, so
 * the same text is validated on two paths: `CronConfigSchema` on the boot sync,
 * and `UpsertConfigBodySchema` on an admin write that round-trips the column.
 * Raising one and not the other makes such a row unsavable through the admin UI
 * — a 400 on a round-trip that changed nothing. That is exactly what a first
 * pass of this change did, and it is the reason the constant is named for the
 * config, not for the cron.
 *
 * 🔴 THIS CAP HAS A FAILURE MODE AND IT IS SILENT. A default that overruns it
 * fails its boot-time SystemConfig sync, the error goes to a log nobody tails,
 * and the cron keeps ticking on the code default — so the only thing actually
 * lost is that cron's admin surface and its per-environment schedule tuning.
 * Four shipped vibe crons spent weeks in that state in 2026-07 for exactly this
 * reason, and `cron-defaults.contract.spec.ts` exists because of it.
 *
 * It was 500, and 500 was not a considered number — it was the default-sized
 * one. The descriptions that matter are the ones for jobs with several
 * independent behaviours to name (the app-backup sweep raises four distinct
 * alarm codes and must say what each one means), and by the time the cap was
 * questioned the top four descriptions in the tree sat at 498, 486, 486 and 484
 * of 500. That is not four verbose sentences, it is a ceiling that had been
 * shaping the writing for months.
 *
 * 2000 is a guess, stated as one: roughly four times the longest description
 * today, which buys a new alarm code its sentence without inviting an essay.
 * The value is exported so nothing has to restate it — the contract spec derives
 * its headroom tripwire from this constant rather than carrying a second copy of
 * the number.
 */
export const CONFIG_DESCRIPTION_MAX_LENGTH = 2000;

/**
 * Cron Config Schema
 *
 * Configuration for scheduled cron jobs.
 */
export const CronConfigSchema = z.object({
  /**
   * Type-safe cron key (DB enum)
   * Used for scheduler registration, logging, lock management, and manual triggers.
   * @example DbEnum.CronKey.EXTERNAL_API_MONITOR
   */
  cronKey: z.nativeEnum(DbEnum.CronKey).optional(),

  /**
   * Human-readable title
   * @example "Gmail OAuth Token Refresh"
   */
  title: z.string().min(1).max(100),

  /**
   * Cron expression
   * - 5-field: minute hour day month weekday
   * - 6-field: second minute hour day month weekday
   */
  schedule: z.string().refine(isValidCronExpression, {
    message:
      "Invalid cron expression. Use 5-field (minute hour day month weekday) or 6-field (second minute hour day month weekday) format"
  }),

  /**
   * Batch size for processing (if applicable)
   * @default varies by job
   */
  batchSize: z.number().int().min(1).max(10000).optional(),

  /**
   * Detailed description of what this cron does.
   * Capped at {@link CONFIG_DESCRIPTION_MAX_LENGTH} — see that constant for why
   * overrunning it is silent, and why the number moved.
   */
  description: z.string().max(CONFIG_DESCRIPTION_MAX_LENGTH).optional(),

  /**
   * Whether this cron is enabled
   * @default true
   */
  enabled: z.boolean().optional()
});

export type CronConfig = z.infer<typeof CronConfigSchema>;

/**
 * Who owns each `CronConfig` field: the stored row an admin edits, or the code.
 *
 *   - `row` — an admin setting. The write path accepts it, the stored row
 *     carries it, and the running system reads it from that row.
 *   - `row-where-declared` — an admin setting for the crons whose code default
 *     declares it, and for no other. `batchSize` is one number some jobs page by
 *     and most jobs have no use for; accepting it on a job that never reads it is
 *     a save that changes nothing.
 *   - `code` — identity and prose that belong to the code. The write path refuses
 *     any value other than the code default (or the row's own unchanged value,
 *     so a round-trip of what an admin loaded still saves), it stores the code
 *     default, and a row carrying anything else is read as the code default.
 *
 * Why `title` and `description` are code-owned: nothing the running system does
 * depends on them, and the one place a cron's title is recorded at run time —
 * `CronExecutionLog.jobTitle` — takes the literal each cron passes to
 * `executeWithCronInfrastructure`, never this field. A title an admin could
 * change would be a second, disagreeing name for the same job. The admin panel
 * reads a cron's description off the code default (`cronDefault`, `codeDefault`). The row's
 * `description` COLUMN is not this field: it is the admin's own note, which
 * nothing in the code writes (`CONFIG_DESCRIPTION_COLUMN_OWNERSHIP`).
 *
 * `satisfies Record<keyof CronConfig, …>` makes this exhaustive: a field added
 * to `CronConfigSchema` without an owner is a compile error here, and
 * `cron-config-fields-are-read.spec.ts` holds each owner to what it claims.
 */
export type CronConfigFieldOwner = "row" | "row-where-declared" | "code";

export const CRON_CONFIG_FIELD_OWNERSHIP = {
  cronKey: "code",
  title: "code",
  description: "code",
  schedule: "row",
  enabled: "row",
  batchSize: "row-where-declared"
} as const satisfies Record<keyof CronConfig, CronConfigFieldOwner>;

/**
 * Every `code` a cron write can be refused with — each a 400 that names the
 * field and what the row accepts instead.
 *
 *   - `CRON_CONFIG_FIELD_CODE_OWNED` — a `code` field sent with a value that is
 *     neither the code default nor the row's own unchanged value.
 *   - `CRON_CONFIG_FIELD_NOT_READ` — a `row-where-declared` field set on a cron
 *     whose code default does not declare it.
 *   - `CRON_CONFIG_KEY_UNREGISTERED` — a write to a cron key with no code default.
 *   - `CRON_CONFIG_ENABLED_CONFLICT` — the `enabled` column and `config.enabled`
 *     both changed, to opposite values.
 *
 * Shared so a client's code-to-sentence map can be exhaustive over it
 * (`Record<CronConfigWriteErrorCode, …>`), and so each backend error class types
 * its `code` against it: a renamed code is a compile error on both sides.
 */
export const CRON_CONFIG_WRITE_ERROR_CODES = [
  "CRON_CONFIG_FIELD_CODE_OWNED",
  "CRON_CONFIG_FIELD_NOT_READ",
  "CRON_CONFIG_KEY_UNREGISTERED",
  "CRON_CONFIG_ENABLED_CONFLICT"
] as const;

export type CronConfigWriteErrorCode = (typeof CRON_CONFIG_WRITE_ERROR_CODES)[number];
